import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import { useAuth } from "../../stores/auth";
import { toast } from "../../stores/toast";
import { Button, Input } from "../../components/ui";
import { PhoneInput } from "../../components/PhoneInput";
import { formatPhoneDisplay } from "../../lib/phoneInput";
import { SlabWall } from "../../components/fx";

type Step = "phone" | "code";

export function VerifyPhone() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser, logout, config } = useAuth();

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentPhone, setSentPhone] = useState("");

  const phoneRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "phone") phoneRef.current?.focus();
    else codeRef.current?.focus();
  }, [step]);

  async function sendCode(e?: React.FormEvent) {
    e?.preventDefault();
    const trimmed = phone.trim();
    if (!trimmed) {
      toast("error", "Enter your mobile number");
      phoneRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await api("/auth/phone/send-code", { body: { phone: trimmed } });
      setSentPhone(trimmed);
      setStep("code");
      setCode("");
      toast("success", "Verification code sent");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Could not send code");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) {
      toast("error", "Enter the verification code");
      codeRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const data = await api<{ user: NonNullable<typeof user> }>("/auth/phone/verify", {
        body: { code: code.trim() },
      });
      await refreshUser();
      if (data.user) {
        useAuth.setState({ user: data.user });
      }
      const from =
        (location.state as { from?: string } | null)?.from ??
        sessionStorage.getItem("gaole_verify_from") ??
        "/";
      sessionStorage.removeItem("gaole_verify_from");
      navigate(from, { replace: true });
      toast("success", "Mobile number verified");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Verification failed");
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    await logout();
    navigate("/login", { replace: true });
  }

  if (!user) {
    return (
      <div className="px-6 py-12 text-center text-sm text-white/60">
        <Link to="/login" className="text-brand-pink hover:underline">
          Sign in
        </Link>{" "}
        to verify your mobile number.
      </div>
    );
  }

  if (user.phoneVerifiedAt && !config?.securityPolicy?.smsOtpEnabled) {
    navigate("/", { replace: true });
    return null;
  }

  if (user.phoneVerifiedAt) {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div className="relative flex min-h-[70vh] flex-col justify-center overflow-hidden px-6 py-8">
      <SlabWall />
      <div className="relative mx-auto w-full max-w-md">
        <div className="glass-panel neon-ring rounded-2xl p-6 shadow-xl sm:p-8">
          <img
            src="/logo-wordmark.png"
            alt="Mr Gaole"
            className="h-9 w-auto max-w-[180px] object-contain object-left"
          />
          <h1 className="mt-6 font-display text-2xl font-bold">Verify mobile</h1>
          <p className="mt-1 text-sm text-white/50">
            {step === "phone"
              ? "Enter your Malaysian mobile number. We will send a one-time code via SMS."
              : `Enter the 6-digit code sent to ${formatPhoneDisplay(sentPhone || phone)}.`}
          </p>

          {step === "phone" ? (
            <form className="mt-6 space-y-4" onSubmit={(e) => void sendCode(e)}>
              <PhoneInput
                numberRef={phoneRef}
                label="Mobile number"
                placeholder="12-345 6789"
                value={phone}
                onChange={setPhone}
                lockDialCode="60"
                hint="Malaysia mobile numbers only."
              />
              <Button type="submit" className="w-full" disabled={busy}>
                Send code
              </Button>
            </form>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={(e) => void verifyCode(e)}>
              <Input
                ref={codeRef}
                label="Verification code"
                placeholder="6-digit code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <Button type="submit" className="w-full" disabled={busy}>
                Verify
              </Button>
              <div className="flex flex-wrap gap-3 text-xs">
                <button
                  type="button"
                  className="text-brand-pink hover:underline"
                  disabled={busy}
                  onClick={() => void sendCode()}
                >
                  Resend code
                </button>
                <button
                  type="button"
                  className="text-white/50 hover:text-white/80"
                  disabled={busy}
                  onClick={() => {
                    setStep("phone");
                    setCode("");
                  }}
                >
                  Change number
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 text-center text-xs text-white/40">
            Wrong account?{" "}
            <button type="button" className="text-brand-pink hover:underline" onClick={() => void signOut()}>
              Sign out
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
